import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { BarChart3, Boxes, ChevronLeft, ChevronRight, ClipboardList, KeyRound, LogOut, Menu, Moon, PackageCheck, PackagePlus, QrCode, RefreshCw, Server, Settings, Sun, Ticket, UsersRound } from "lucide-react";
import { useTheme } from "next-themes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { queryClient } from "@/app/query-client";
import { env } from "@/app/env";
import { useAuth } from "@/auth/use-auth";
import { isAdmin, isReleaseApprover } from "@/auth/capabilities";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLayoutStore } from "@/stores/layout-store";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/clients", key: "clientsTargets", icon: UsersRound, adminOnly: false, approverOnly: false, surface: "tenant" },
  { to: "/registration-requests", key: "registrationRequests", icon: QrCode, adminOnly: false, approverOnly: false, surface: "tenant" },
  { to: "/product-activations", key: "productActivations", icon: PackagePlus, adminOnly: false, approverOnly: false, surface: "tenant" },
  { to: "/release-publication-requests", key: "releasePublication", icon: PackageCheck, adminOnly: false, approverOnly: true, surface: "catalog" },
  { to: "/apps", key: "apps", icon: Boxes, adminOnly: true, approverOnly: false, surface: "catalog" },
  { to: "/enrollments", key: "enrollments", icon: Ticket, adminOnly: true, approverOnly: false, surface: "tenant" },
  { to: "/stations", key: "stations", icon: Server, adminOnly: true, approverOnly: false, surface: "tenant" },
  { to: "/audit", key: "audit", icon: ClipboardList, adminOnly: true, approverOnly: false, surface: "tenant" },
  { to: "/reports", key: "reports", icon: BarChart3, adminOnly: true, approverOnly: false, surface: "tenant" },
  { to: "/settings", key: "settings", icon: Settings, adminOnly: true, approverOnly: false, surface: "catalog" }
] as const;

export function AdminShell() {
  const { t, i18n } = useTranslation("common");
  const auth = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const { sidebarCollapsed, toggleSidebar } = useLayoutStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const title = useMemo(() => pageTitle(pathname, t), [pathname, t]);
  const visibleNavItems = navItems.filter((item) => {
    if (env.uiMode !== "legacy" && item.surface !== env.uiMode) return false;
    if (isAdmin(auth.roles)) return true;
    if (isReleaseApprover(auth.roles)) return item.approverOnly;
    return !item.adminOnly && !item.approverOnly;
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 hidden border-r bg-panel transition-[width] lg:block",
          sidebarCollapsed ? "w-16" : "w-64"
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b px-3">
          <KeyRound className="h-5 w-5 text-primary" />
          {!sidebarCollapsed ? <span className="truncate text-sm font-semibold">{t("appName")}</span> : null}
        </div>
        <nav className="grid gap-1 p-2">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const label = t(`nav.${item.key}`);
            return (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>
                  <Link
                    to={item.to}
                    className={cn(
                      "flex h-9 items-center gap-2 rounded-md px-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                      sidebarCollapsed && "justify-center",
                      pathname.startsWith(item.to) && "bg-accent text-accent-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!sidebarCollapsed ? <span className="truncate">{label}</span> : null}
                  </Link>
                </TooltipTrigger>
                {sidebarCollapsed ? <TooltipContent side="right">{label}</TooltipContent> : null}
              </Tooltip>
            );
          })}
        </nav>
        <Button className="absolute bottom-3 left-3" size="icon" variant="ghost" onClick={toggleSidebar}>
          {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </aside>

      <div className={cn("min-h-screen transition-[padding]", sidebarCollapsed ? "lg:pl-16" : "lg:pl-64")}>
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-panel/95 px-4 backdrop-blur">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button className="lg:hidden" size="icon" variant="ghost" aria-label={t("nav.openMenu")}>
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent className="left-0 top-0 h-dvh w-[min(86vw,320px)] -translate-x-0 -translate-y-0 content-start overflow-y-auto rounded-none border-y-0 border-l-0 p-0">
              <div className="flex h-14 items-center gap-2 border-b px-4">
                <KeyRound className="h-5 w-5 text-primary" />
                <DialogTitle className="truncate text-sm font-semibold">{t("appName")}</DialogTitle>
              </div>
              <nav className="grid gap-1 p-2" aria-label={t("nav.mobileLabel")}>
                {visibleNavItems.map((item) => {
                  const Icon = item.icon;
                  const label = t(`nav.${item.key}`);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex min-h-11 items-center gap-3 rounded-md px-3 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                        pathname.startsWith(item.to) && "bg-accent text-accent-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="min-w-0 truncate">{label}</span>
                    </Link>
                  );
                })}
              </nav>
              <div className="grid gap-2 border-t p-2 sm:hidden">
                <Button variant="ghost" className="justify-start" onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>
                  {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {t("theme.toggle")}
                </Button>
                <Select value={i18n.language.slice(0, 2)} onValueChange={(value) => void i18n.changeLanguage(value)}>
                  <SelectTrigger className="w-full" aria-label={t("locale.label")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ru">RU</SelectItem>
                    <SelectItem value="en">EN</SelectItem>
                    <SelectItem value="be">BE</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" className="min-w-0 justify-start" onClick={auth.logout}>
                  <LogOut className="h-4 w-4" />
                  <span className="truncate">{auth.userName || "account"}</span>
                  <span className="ml-auto">{t("actions.signOut")}</span>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-semibold">{title}</h1>
          </div>
          <Button
            size="icon"
            variant="ghost"
            aria-label={t("actions.refresh")}
            onClick={() => {
              void queryClient.invalidateQueries();
            }}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button className="hidden sm:inline-flex" size="icon" variant="ghost" aria-label={t("theme.toggle")} onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>
            {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Select value={i18n.language.slice(0, 2)} onValueChange={(value) => void i18n.changeLanguage(value)}>
            <SelectTrigger className="hidden w-28 sm:flex" aria-label={t("locale.label")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ru">RU</SelectItem>
              <SelectItem value="en">EN</SelectItem>
              <SelectItem value="be">BE</SelectItem>
            </SelectContent>
          </Select>
          <div className="hidden sm:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="max-w-48 truncate">
                  {auth.userName || "account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={auth.logout}>
                  <LogOut className="h-4 w-4" />
                  {t("actions.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="mx-auto grid w-full max-w-[1440px] gap-5 px-4 py-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function pageTitle(pathname: string, t: (key: string) => string) {
	if (pathname === "/clients" || pathname.includes("/clients/")) {
	  return t("nav.clientsTargets");
	}
	if (pathname.includes("/product-activations")) {
	  return t("nav.productActivations");
	}
	if (pathname.includes("/release-publication-requests")) {
	  return t("nav.releasePublication");
	}
	if (pathname.includes("/registration-requests")) {
	  return t("nav.registrationRequests");
	}
  if (pathname.includes("/stations")) {
    return t("nav.stations");
  }
  if (pathname.includes("/enrollments")) {
    return t("nav.enrollments");
  }
  if (pathname.includes("/audit")) {
    return t("nav.audit");
  }
  if (pathname.includes("/reports")) {
    return t("nav.reports");
  }
  if (pathname.includes("/settings")) {
    return t("nav.settings");
  }
  if (pathname.includes("/apps")) {
    return t("nav.apps");
  }
  return t("nav.overview");
}
